import React from 'react';
import { Form } from 'antd';
import { useStore } from 'routes/inventorize_meter/useStore';
import { booleanToSelectValue } from 'utils/booleanToSelectValue';
import { ThermalDetails } from 'components/Form/meter_details/thermal/ThermalColdDetails';
import { ThermalMeterDetailField } from 'components/Form/meter_details/thermal/constants';
import { FormItemProps } from 'components/Form/FormItem';
import { useIntl } from 'react-intl';
import { Nullable } from '@enmon/commons';
import { MeterDetailThermal } from '@enmon/commons/lib/enmon';
import { Merge } from 'type-fest';
import { validateFlowrate } from '../utils/validateFlowrate';
import { ThermalAdditionalFields as messages } from '../messages';

export type MeterThermalAdditionalDetails = Merge<
  MeterDetailThermal,
  {
    flowrate?: {
      value: Nullable<number>;
      unit: Nullable<string>;
    } | null;
    flange?: number | null;
  }
>;

export type MeterThermalDetailsProps = MeterThermalAdditionalDetails & {
  onFormItemReset: FormItemProps['onResetField'];
  meterId?: number;
};

/* eslint-disable camelcase */
export const ThermalAdditionalFields: React.VFC<MeterThermalDetailsProps> = props => {
  const { meterId, onFormItemReset, ...restTypeDetails } = props;
  const intl = useIntl();

  const {
    diameterNominal,
    flange,
    flowrate,
    layLength,
    installationPosition,
    orientation,
    resistance,
    thermalMeasurementType,
  } = restTypeDetails;

  const form = Form.useFormInstance();
  const flowrateUnit = Form.useWatch<Nullable<string>>(['detail', 'flowrate', 'unit'], form) ?? flowrate?.unit;
  const flowrateValue = Form.useWatch<Nullable<number>>(['detail', 'flowrate', 'value'], form) ?? flowrate?.value;

  const { setDetailStepValues, newMeter } = useStore();

  // Has to be done in this way because of the typecasting in parent component
  const handleFlowrateReset = () => {
    const emptyFlowrateValue = {
      detail: { ...restTypeDetails, flowrate: { value: null, unit: null } } as MeterThermalAdditionalDetails,
    };

    form.setFieldsValue({ detail: { flowrate: { value: null, unit: null } } });

    setDetailStepValues(emptyFlowrateValue);
  };

  return (
    <ThermalDetails
      itemsConfig={{
        [ThermalMeterDetailField.Flange]: {
          formItemProps: {
            initialValue: booleanToSelectValue(flange),
            isSemiRequired: !meterId,
            onResetField: onFormItemReset,
          },
        },
        [ThermalMeterDetailField.Orientation]: {
          formItemProps: { initialValue: orientation, isSemiRequired: !meterId, onResetField: onFormItemReset },
        },
        [ThermalMeterDetailField.ThermalMeasurementType]: {
          formItemProps: {
            initialValue: thermalMeasurementType,
            isSemiRequired: !meterId,
            onResetField: onFormItemReset,
          },
        },
        ...(!newMeter && {
          [ThermalMeterDetailField.Flowrate]: {
            formItemProps: {
              // Custom validation is used only in create mode
              rules: !meterId
                ? [
                    {
                      required: !meterId,
                      message: intl.formatMessage(messages.flowRateRequired),
                    },
                    validateFlowrate,
                  ]
                : [],
              isSemiRequired: !meterId,
              onResetField: handleFlowrateReset,
            },
            additionalConfig: { flowrateUnit, flowrateValue },
          },
        }),
        [ThermalMeterDetailField.LayLength]: {
          formItemProps: { initialValue: layLength, isSemiRequired: !meterId, onResetField: onFormItemReset },
        },
        [ThermalMeterDetailField.DiameterNominal]: {
          formItemProps: { initialValue: diameterNominal, isSemiRequired: !meterId, onResetField: onFormItemReset },
        },
        ...(!newMeter && {
          [ThermalMeterDetailField.Resistance]: {
            formItemProps: { initialValue: resistance, isSemiRequired: !meterId, onResetField: onFormItemReset },
          },
          [ThermalMeterDetailField.InstallationPosition]: {
            formItemProps: {
              initialValue: installationPosition,
              isSemiRequired: !meterId,
              onResetField: onFormItemReset,
            },
          },
        }),
      }}
    />
  );
};
